import type { V1Automation } from '@/generated/hooks/grimoire/models/v1Automation';
import type { V1ClarifyingQuestion } from '@/generated/hooks/zen/models';
import type { V1Exception } from '@/generated/hooks/astral/models';
import { createStore } from './config';
import { getCurrentDate } from '@/shared/utils/datetime-utils';
import type { IAutomationBook } from '@/modules/automation/components/chat/interface';

/**
 * Automation Store
 * Manages the state of the current process being edited
 */

interface AutomationState {
  automation: V1Automation | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  clarifyingQuestions: V1ClarifyingQuestion[] | null;
  integrations: IAutomationBook[];
  exception: Partial<V1Exception> | null;

  // Actions
  updateAutomation: (automation: Partial<V1Automation> | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
  setClarifyingQuestions: (
    clarifyingQuestions: V1ClarifyingQuestion[] | null,
  ) => void;
  setIntegrations: (integrations: IAutomationBook[]) => void;
  setException: (exception: Partial<V1Exception> | null) => void;
}

export const useAutomationStore = createStore<AutomationState>(
  'automation-store',
  (_, get, api) => ({
    automation: null,
    isLoading: false,
    error: null,
    lastUpdated: null,
    clarifyingQuestions: null,
    integrations: [],
    exception: null,

    // Actions with DevTools naming
    updateAutomation: (automation: Partial<V1Automation> | null) => {
      api.setState(
        {
          automation: automation
            ? ({ ...get().automation, ...automation } as V1Automation)
            : null,
          lastUpdated: getCurrentDate(),
          error: null,
        },
        false,
        {
          type: 'automation/updateAutomation',
          automation,
        },
      );
    },

    setLoading: (isLoading: boolean) => {
      api.setState({ isLoading }, false, {
        type: 'automation/setLoading',
        isLoading,
      });
    },

    setError: (error: string | null) => {
      api.setState({ error }, false, {
        type: 'automation/setError',
        error,
      });
    },

    reset: () => {
      api.setState(
        {
          automation: null,
          isLoading: false,
          error: null,
          lastUpdated: null,
          clarifyingQuestions: null,
          integrations: [],
          exception: null,
        },
        false,
        'automation/reset',
      );
    },

    setClarifyingQuestions: (
      clarifyingQuestions: V1ClarifyingQuestion[] | null,
    ) => {
      api.setState({ clarifyingQuestions }, false, {
        type: 'automation/setClarifyingQuestions',
        clarifyingQuestions,
      });
    },

    setIntegrations: (integrations: IAutomationBook[]) => {
      api.setState({ integrations }, false, {
        type: 'automation/setIntegrations',
        integrations,
      });
    },

    setException: (exception: Partial<V1Exception> | null) => {
      api.setState({ exception }, false, {
        type: 'automation/run/setException',
        exception,
      });
    },
  }),
);

export const selectAutomation = (state: AutomationState) => state.automation;
export const selectAutomationName = (state: AutomationState) =>
  state.automation?.name;
export const selectAutomationDisplayName = (state: AutomationState) =>
  state.automation?.display_name;
export const selectAutomationCode = (state: AutomationState) =>
  state.automation?.code; // SPy code
export const selectAutomationInputSpecs = (state: AutomationState) =>
  state.automation?.input_specs;
export const selectIsLoading = (state: AutomationState) => state.isLoading;
export const selectError = (state: AutomationState) => state.error;
export const selectLastUpdated = (state: AutomationState) => state.lastUpdated;

export const selectSetLoading = (state: AutomationState) => state.setLoading;
export const selectSetError = (state: AutomationState) => state.setError;
export const selectReset = (state: AutomationState) => state.reset;
export const selectClarifyingQuestions = (state: AutomationState) =>
  state.clarifyingQuestions;
export const selectSetClarifyingQuestions = (state: AutomationState) =>
  state.setClarifyingQuestions;
export const selectException = (state: AutomationState) => state.exception;
export const selectSetException = (state: AutomationState) =>
  state.setException;
export const selectAutomationAOP = (state: AutomationState) => {
  if (state.automation?.english_code) {
    return state.automation.english_code;
  }

  return '';
};

export const selectUpdateAutomation = (state: AutomationState) =>
  state.updateAutomation;

export const selectIntegrations = (state: AutomationState) =>
  state.integrations;
export const selectSetIntegrations = (state: AutomationState) =>
  state.setIntegrations;

export const selectAutomationSetLoading = (state: AutomationState) =>
  state.setLoading;
export const selectAutomationSetError = (state: AutomationState) =>
  state.setError;
export const selectCodeMapping = (state: AutomationState) =>
  state.automation?.code_mapping;
